import React, { memo, ReactNode, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { useTranslation } from 'react-i18next';
import useScreenSize from '../../../hooks/useScreenSize';
import { useGPTScript } from '../../../hooks/useGPTScript';
import { HeaderWrapper, LayoutWrapper } from './styled';
import { CurrentItemHeader } from '../Sections/CurrentItemHeader';
import { ItemStatusEnum } from '../../../graphql/types';
import { WebViewAdAppBar } from '../../WebView/AdAppBar';
import { CurrentItemQuery } from '../../../graphql/graphql';
import { useClient } from '../../../hooks/useClient';
import { normalizeTargetingParameter } from '../../../utils/helpers/normalizeTargetingParameter';
import { transliterateCyrillicToLatin } from '../../../utils/helpers/transliterateCyrillicToLatin';
import {
  AdriverTargetingProps,
  useAdriverTargeting
} from '../../../hooks/useTargetedBanner/useAdriverTargeting';

const Banner = dynamic(() => import('../../Banner'), {
  ssr: false
});

const AdManagerBanner = dynamic(() => import('../../Banner/AdManagerBanner'), {
  ssr: false
});

export interface CurrentItemLayoutProps {
  children: ReactNode;
  currentItem?: CurrentItemQuery['item'];
  isResidencePage?: boolean;
  isFavorited: boolean;
  setFavorited: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CurrentItemLayout: React.FC<CurrentItemLayoutProps> = memo(
  ({
    children,
    currentItem,
    isResidencePage = false,
    isFavorited,
    setFavorited
  }) => {
    const { isWebView } = useBridgeState();
    const { isSmall } = useScreenSize();
    const isClient = useClient();
    const {
      i18n: { language }
    } = useTranslation();

    useGPTScript();

    const cityName = currentItem?.city?.name || '';
    const cityId = currentItem?.city?.id || '';
    const adriverCityName =
      language === 'az'
        ? normalizeTargetingParameter(cityName)
        : transliterateCyrillicToLatin(cityId, cityName);

    useAdriverTargeting({
      leased: currentItem?.leased,
      rooms: currentItem?.rooms,
      categoryId: currentItem?.category?.id,
      hasRepair: !!currentItem?.hasRepair,
      cityName: adriverCityName,
      price: currentItem?.price?.total,
      hasBillOfSale: !!currentItem?.hasBillOfSale,
      hasMortgage: !!currentItem?.hasMortgage,
      shouldNotRunOnRouter: true
    } as AdriverTargetingProps); // TODO: fix types

    useEffect(() => {
      setFavorited?.(!!currentItem?.isBookmarked);
    }, [currentItem?.isBookmarked]);

    const isPublished = currentItem?.state === ItemStatusEnum.Published;
    const isExpired = currentItem?.state === ItemStatusEnum.Expired;
    const isExpiredManually = isExpired && currentItem?.isExpiredManually;
    const isAuthor = currentItem?.isMine;

    const isExpiredAndMine = (isExpired || isExpiredManually) && isAuthor;

    const header = isWebView ? (
      <WebViewAdAppBar setFavorited={setFavorited} isBookmarked={isFavorited} />
    ) : (
      <CurrentItemHeader
        isBookmarked={isFavorited}
        setFavorited={setFavorited}
        logoDataStat="item-logo-head-mob"
        isBookmarkVisible={isPublished}
        isShareVisible={Boolean(isPublished || isExpiredAndMine)}
      />
    );

    return (
      <LayoutWrapper $isResidencePage={isResidencePage}>
        {currentItem && isSmall && isClient && (
          <>
            <Banner
              key="lotriver-top-banner"
              containerClassName="lotriver-top-banner"
              containerId="js-lotriver-top-banner"
              sid="220686"
              bn="2"
              arCn={1}
              target={currentItem}
              shouldNotRunOnRouter
            />
            <AdManagerBanner bannerName="top" />
          </>
        )}

        {!isResidencePage && <HeaderWrapper>{header}</HeaderWrapper>}

        {children}
      </LayoutWrapper>
    );
  }
);

CurrentItemLayout.displayName = `CurrentItemLayout`;
